/**
 * 演示 / UI 单测用的本地后端：在单浏览器内模拟一整场训练。
 *
 * 不建立任何网络连接、不依赖真实时钟服务；用于：
 * - `?demo=1` 离线演示完整界面（含发牌、计时、揭示、战报）；
 * - UI 组件测试与截图测试。
 * 生产房间请使用 P2PRoomBackend（src/state/p2pRoomBackend.ts）。
 */
import {
  AI_GLASSES_MOTION,
  getPairDealPlans,
  getPairStages,
  getStage,
  sideLabel,
  type AssertionAlert,
  type ConstructiveStrategyCard,
  type CrossExTaskCard,
  type DealtCard,
  type MatchPhase,
  type MotionPackage,
  type Participant,
  type RevealedCard,
  type RoomAnnotation,
  type RoomEvent,
  type SandboxCard,
  type Side,
  type StageDefinition,
} from '@zongheng/core-schema';
import { makeParticipantId } from '../app/identity.ts';
import type {
  ArenaUiState,
  ConnectionStatus,
  IdentityInput,
  RemoteMediaStream,
  RoomBackend,
  ToastMessage,
} from './types.ts';

const CAPABILITY_NOTES = [
  'Phase 1：纯端侧运行，P2P 媒体直连 + 教师中心信令',
  'ASR / LLM / TTS 为 Phase 2 预留插槽，当前由真人手动输入模拟',
  '演示模式：本页所有数据由本地 Mock 后端生成，不联网',
];

function emptyState(): ArenaUiState {
  return {
    route: 'home',
    roomId: null,
    self: null,
    connection: 'idle',
    connectionDetail: '尚未连接房间',
    participants: [],
    phase: 'LOBBY',
    stageIndex: 0,
    stage: null,
    stagePhase: 'SPEAKING',
    prepRemainingMs: 0,
    remainingMs: { AFF: 0, NEG: 0 },
    runningSide: null,
    completedStages: [],
    commitments: {},
    reveals: [],
    dealtCards: [],
    alerts: [],
    annotations: [],
    eventLog: [],
    manualEvidenceChecks: { AFF: [], NEG: [] },
    search: { open: false, query: '', hits: [], activeIndex: 0 },
    transcriptDraft: '',
    transcriptOpen: false,
    toasts: [],
    peering: {
      connectedPeers: 0,
      totalPeers: 0,
      mediaEnabled: false,
      micEnabled: false,
      cameraEnabled: false,
      deafened: false,
      note: '演示模式未启用媒体采集',
    },
    vadMode: 'unavailable',
    speechStats: [],
    startedAt: null,
    finishedAt: null,
    capabilityNotes: CAPABILITY_NOTES,
  };
}

function stageOf(index: number): StageDefinition | null {
  if (index < 1) return null;
  try {
    return getStage(index);
  } catch {
    return null;
  }
}

/** 环节预算（秒）→ UI 计时（毫秒） */
function budgetsMs(stage: StageDefinition): Record<Side, number> {
  return { AFF: stage.budgets.AFF * 1000, NEG: stage.budgets.NEG * 1000 };
}

let eventSeq = 0;
function event(type: RoomEvent['type'], stageIndex: number, extra: Partial<RoomEvent> = {}): RoomEvent {
  eventSeq += 1;
  return {
    id: `mock-event-${eventSeq}`,
    type,
    stageIndex,
    at: Date.now(),
    ...extra,
  };
}

export class MockRoomBackend implements RoomBackend {
  role: 'TEACHER' | 'DEBATER' | null = null;

  private state: ArenaUiState = emptyState();
  private listeners = new Set<() => void>();
  private timer: ReturnType<typeof setInterval> | null = null;
  private lastTickAt = 0;
  private matchStartedAt = 0;
  private motion: MotionPackage;

  constructor(motion: MotionPackage = AI_GLASSES_MOTION) {
    this.motion = motion;
  }

  getState(): ArenaUiState {
    return this.state;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  dispose(): void {
    this.stopTimer();
    this.listeners.clear();
  }

  /* ------------------------------ 房间 ------------------------------ */

  async createRoom(_identity: IdentityInput, roomCode: string): Promise<void> {
    this.role = 'TEACHER';
    const self: Participant = {
      id: makeParticipantId(),
      name: '教练（演示）',
      role: 'TEACHER',
      joinedAt: Date.now(),
    };
    const bots: Participant[] = [
      { id: 'mock-aff-1', name: '正方一辩（演示）', role: 'DEBATER', side: 'AFF', seat: 1, joinedAt: Date.now() },
      { id: 'mock-neg-2', name: '反方二辩（演示）', role: 'DEBATER', side: 'NEG', seat: 2, joinedAt: Date.now() },
      { id: 'mock-aff-2', name: '正方二辩（演示）', role: 'DEBATER', side: 'AFF', seat: 2, joinedAt: Date.now() },
    ];
    this.update({
      route: 'coach',
      roomId: roomCode,
      self,
      connection: 'connected',
      connectionDetail: '演示房间已就绪（本地 Mock）',
      participants: [self, ...bots],
      eventLog: [event('ROOM_CREATED', 0), ...bots.map(() => event('PARTICIPANT_JOINED', 0))],
    });
    this.toast('success', `演示房间 ${roomCode} 已创建`);
  }

  async joinRoom(roomCode: string, identity: IdentityInput): Promise<void> {
    this.role = identity.role;
    const self: Participant = {
      id: makeParticipantId(),
      name: identity.name,
      role: identity.role,
      side: identity.side,
      seat: identity.seat,
      joinedAt: Date.now(),
    };
    const teacher: Participant = {
      id: 'mock-teacher',
      name: '教练（演示）',
      role: 'TEACHER',
      joinedAt: Date.now(),
    };
    const opponentSide: Side = identity.side === 'NEG' ? 'AFF' : 'NEG';
    const opponent: Participant = {
      id: 'mock-opponent',
      name: `${sideLabel(opponentSide)}辩手（演示）`,
      role: 'DEBATER',
      side: opponentSide,
      seat: 2,
      joinedAt: Date.now(),
    };
    this.update({
      route: identity.role === 'TEACHER' ? 'coach' : 'arena',
      roomId: roomCode,
      self,
      connection: 'connected',
      connectionDetail: '演示房间已就绪（本地 Mock）',
      participants: [teacher, self, opponent],
      peering: {
        connectedPeers: 2,
        totalPeers: 2,
        mediaEnabled: false,
        micEnabled: false,
        cameraEnabled: false,
        deafened: false,
        note: '演示模式未启用媒体采集',
      },
      eventLog: [event('PARTICIPANT_JOINED', 0)],
    });
    this.toast('success', `已加入演示房间 ${roomCode}`);
  }

  leaveRoom(): void {
    this.stopTimer();
    this.role = null;
    this.update(emptyState());
  }

  /* ---------------------------- 教师控制 ---------------------------- */

  startMatch(): void {
    if (this.role !== 'TEACHER') {
      this.toast('warning', '仅教师端可以开始比赛');
      return;
    }
    this.matchStartedAt = Date.now();
    const firstStage = stageOf(1) as StageDefinition;
    this.update({
      phase: 'RUNNING',
      startedAt: this.matchStartedAt,
      finishedAt: null,
      stageIndex: 1,
      stage: firstStage,
      stagePhase: 'SPEAKING',
      completedStages: [],
      remainingMs: budgetsMs(firstStage),
      runningSide: 'AFF',
      eventLog: [...this.state.eventLog, event('MATCH_STARTED', 0), event('STAGE_STARTED', 1)],
    });
    this.dealForStage(1);
    this.startTimer();
    this.toast('info', '比赛开始：环节 1 正方一辩申论');
  }

  advanceStage(): void {
    if (this.role !== 'TEACHER') return;
    if (this.state.phase === 'FINISHED') return;
    if (this.state.stageIndex === 0) {
      this.startMatch();
      return;
    }
    const current = this.state.stage;
    if (!current) return;

    if (this.state.stagePhase === 'PREP') {
      this.update({ stagePhase: 'SPEAKING', prepRemainingMs: 0, runningSide: current.startSide });
      return;
    }

    if (this.state.stagePhase === 'SPEAKING') {
      this.endStage(false);
      return;
    }

    // ENDED → 下一环节
    const nextIndex = current.index + 1;
    if (nextIndex > 16) {
      this.update({
        phase: 'FINISHED',
        finishedAt: Date.now(),
        runningSide: null,
        eventLog: [...this.state.eventLog, event('MATCH_FINISHED', 16)],
      });
      this.stopTimer();
      this.toast('success', '比赛结束，可查看复盘与导出战报');
      return;
    }
    const next = stageOf(nextIndex) as StageDefinition;
    this.update({
      stageIndex: nextIndex,
      stage: next,
      stagePhase: next.prepSec > 0 ? 'PREP' : 'SPEAKING',
      prepRemainingMs: next.prepSec * 1000,
      remainingMs: budgetsMs(next),
      runningSide: next.prepSec > 0 ? null : next.startSide,
      eventLog: [...this.state.eventLog, event('STAGE_STARTED', nextIndex)],
    });
    this.dealForStage(nextIndex);
    this.startTimer();
  }

  pauseMatch(): void {
    if (this.state.phase !== 'RUNNING') return;
    this.update({ phase: 'PAUSED', runningSide: null });
  }

  resumeMatch(): void {
    if (this.state.phase !== 'PAUSED') return;
    const stage = this.state.stage;
    this.update({ phase: 'RUNNING', runningSide: stage?.startSide ?? 'AFF' });
    this.startTimer();
  }

  resetMatch(): void {
    this.stopTimer();
    this.update({
      ...emptyState(),
      route: this.role === 'TEACHER' ? 'coach' : 'arena',
      roomId: this.state.roomId,
      self: this.state.self,
      connection: 'connected',
      connectionDetail: this.state.connectionDetail,
      participants: this.state.participants,
      capabilityNotes: CAPABILITY_NOTES,
    });
  }

  forceReveal(): void {
    if (this.state.stageIndex < 1) return;
    this.revealPairForStage(this.state.stageIndex);
  }

  /* ---------------------------- 辩手操作 ---------------------------- */

  toggleSpeaker(side: Side): void {
    if (this.state.phase !== 'RUNNING' || this.state.stagePhase !== 'SPEAKING') return;
    if (this.state.stage && !this.state.stage.allowToggle) return;
    if (this.state.runningSide === side) return;
    this.update({
      runningSide: side,
      eventLog: [...this.state.eventLog, event('CLOCK_TOGGLED', this.state.stageIndex, { side })],
    });
  }

  toggleEvidence(side: Side, evidenceId: string): void {
    const current = this.state.manualEvidenceChecks[side] ?? [];
    const next = current.includes(evidenceId)
      ? current.filter((id) => id !== evidenceId)
      : [...current, evidenceId];
    this.update({
      manualEvidenceChecks: { ...this.state.manualEvidenceChecks, [side]: next },
      eventLog: [...this.state.eventLog, event('EVIDENCE_CHECKED', this.state.stageIndex, { side })],
    });
  }

  submitTranscript(side: Side, text: string): void {
    this.update({ transcriptDraft: '' });
    if (!text.trim()) return;
    // Mock 后端用简单正则复刻断言器的「抓错」能力，便于 UI 演示。
    for (const evidence of this.motion.evidenceLibrary) {
      for (const misquote of evidence.forbiddenMisquotes) {
        let regex: RegExp;
        try {
          regex = new RegExp(misquote.pattern, 'u');
        } catch {
          continue;
        }
        const match = regex.exec(text);
        if (match) {
          const alert: AssertionAlert = {
            id: `mock-alert-${Date.now()}-${evidence.id}`,
            side,
            evidenceId: evidence.id,
            severity: 'VIOLATION',
            message: misquote.correctionAlert,
            snippet: text.slice(Math.max(0, match.index - 12), match.index + match[0].length + 12),
            matchedText: match[0],
            stageIndex: this.state.stageIndex || 1,
            at: Date.now(),
          };
          this.update({
            alerts: [...this.state.alerts, alert],
            eventLog: [...this.state.eventLog, event('ASSERTION_ALERT', alert.stageIndex, { side })],
          });
          this.toast('warning', `断言引擎：${misquote.correctionAlert}`);
          return;
        }
      }
    }
    // 命中必打论据则勾选
    for (const evidence of this.motion.evidenceLibrary) {
      const hit = evidence.mustIncludeKeywords.some((keyword) => text.includes(keyword));
      if (hit && evidence.id) {
        this.toggleEvidence(side, evidence.id);
      }
    }
    this.toast('info', '已模拟录入一段发言文本（未发现口径错误）');
  }

  addAnnotation(text: string): void {
    if (!text.trim() || !this.state.self) return;
    const annotation: RoomAnnotation = {
      id: `mock-annotation-${Date.now()}`,
      authorId: this.state.self.id,
      authorName: this.state.self.name,
      text: text.trim(),
      at: Date.now(),
      stageIndex: this.state.stageIndex,
    };
    this.update({
      annotations: [...this.state.annotations, annotation],
      eventLog: [...this.state.eventLog, event('ANNOTATION_ADDED', this.state.stageIndex)],
    });
    this.toast('success', '批注已记录');
  }

  /* ----------------------------- 搜索 ----------------------------- */

  openSearch(query = ''): void {
    this.update({ search: { open: true, query, hits: this.searchDocs(query), activeIndex: 0 } });
  }

  closeSearch(): void {
    this.update({ search: { ...this.state.search, open: false } });
  }

  setSearchQuery(query: string): void {
    this.update({ search: { ...this.state.search, query, hits: this.searchDocs(query), activeIndex: 0 } });
  }

  moveSearchSelection(delta: number): void {
    const { hits, activeIndex } = this.state.search;
    if (hits.length === 0) return;
    const next = (activeIndex + delta + hits.length) % hits.length;
    this.update({ search: { ...this.state.search, activeIndex: next } });
  }

  setTranscriptOpen(open: boolean): void {
    this.update({ transcriptOpen: open });
  }

  setTranscriptDraft(text: string): void {
    this.update({ transcriptDraft: text });
  }

  dismissToast(id: string): void {
    this.update({ toasts: this.state.toasts.filter((t) => t.id !== id) });
  }

  async enableMedia(): Promise<void> {
    this.toast('info', '演示模式不启用真实媒体采集');
  }

  async setCameraEnabled(_enabled: boolean): Promise<void> {
    this.toast('info', '演示模式不启用摄像头');
  }

  async setMicrophoneEnabled(_enabled: boolean): Promise<void> {
    this.toast('info', '演示模式不启用麦克风');
  }

  setDeafened(deafened: boolean): void {
    this.update({ peering: { ...this.state.peering, deafened } });
  }

  getRemoteStreams(): RemoteMediaStream[] {
    return [];
  }

  getLocalStream(): MediaStream | null {
    return null;
  }

  /* ----------------------------- 战报 ----------------------------- */

  async exportReport(): Promise<string> {
    const report = buildMockReport(this.motion, this.state);
    await copyText(report);
    this.toast('success', 'Markdown 战报已复制到剪贴板（演示模式）');
    return report;
  }

  setRoute(route: ArenaUiState['route']): void {
    this.update({ route });
  }

  /* ---------------------------- 内部实现 ---------------------------- */

  private update(partial: Partial<ArenaUiState>): void {
    this.state = { ...this.state, ...partial };
    for (const listener of this.listeners) listener();
  }

  private toast(kind: ToastMessage['kind'], text: string): void {
    const toast: ToastMessage = { id: `toast-${Date.now()}-${Math.random()}`, kind, text };
    this.update({ toasts: [...this.state.toasts, toast].slice(-4) });
    setTimeout(() => this.dismissToast(toast.id), 4200);
  }

  private startTimer(): void {
    if (this.timer) return;
    this.lastTickAt = Date.now();
    this.timer = setInterval(() => this.tick(), 100);
  }

  private stopTimer(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  private tick(): void {
    const now = Date.now();
    const delta = now - this.lastTickAt;
    this.lastTickAt = now;
    const stage = this.state.stage;
    if (!stage || this.state.phase !== 'RUNNING') return;

    if (this.state.stagePhase === 'PREP') {
      const prepRemainingMs = Math.max(0, this.state.prepRemainingMs - delta);
      if (prepRemainingMs === 0) {
        this.update({
          stagePhase: 'SPEAKING',
          prepRemainingMs: 0,
          runningSide: stage.startSide,
        });
      } else {
        this.update({ prepRemainingMs });
      }
      return;
    }

    if (this.state.stagePhase !== 'SPEAKING' || !this.state.runningSide) return;
    const side = this.state.runningSide;
    const remaining = Math.max(0, this.state.remainingMs[side] - delta);
    const remainingMs = { ...this.state.remainingMs, [side]: remaining };
    if (remaining > 0) {
      this.update({ remainingMs });
      return;
    }
    // 时间耗尽
    if (stage.clockMode === 'SINGLE') {
      this.update({ remainingMs });
      this.endStage(true);
      return;
    }
    const other: Side = side === 'AFF' ? 'NEG' : 'AFF';
    if (remainingMs[other] > 0) {
      this.update({ remainingMs, runningSide: other });
      this.toast('warning', `${sideLabel(side)}时间用尽，自动切换发言权`);
    } else {
      this.update({ remainingMs });
      this.endStage(true);
    }
  }

  private endStage(auto: boolean): void {
    const stage = this.state.stage;
    if (!stage) return;
    this.update({
      stagePhase: 'ENDED',
      runningSide: null,
      completedStages: this.state.completedStages.includes(stage.index)
        ? this.state.completedStages
        : [...this.state.completedStages, stage.index],
      eventLog: [...this.state.eventLog, event('STAGE_ENDED', stage.index)],
    });
    this.revealPairForStage(stage.index);
    if (auto) this.toast('info', `环节 ${stage.index}「${stage.name}」结束，双方底牌已揭示`);
  }

  private dealForStage(stageIndex: number): void {
    const stage = stageOf(stageIndex);
    if (!stage || !stage.cardSlot) return;
    const dealt: DealtCard[] = [];
    if (stage.cardSlot === 'CONSTRUCTIVE') {
      const card = this.motion.constructiveCards.find((c) => c.stageIndex === stageIndex);
      if (card) dealt.push({ kind: 'CONSTRUCTIVE', card });
    } else if (stage.cardSlot === 'CROSS_EXAM') {
      const card = this.motion.crossExCards.find((c) => c.stageIndex === stageIndex);
      if (card) dealt.push({ kind: 'CROSS_EXAM', card });
    } else if (stage.cardSlot === 'SANDBOX') {
      const index = stage.pairId === 'D2' ? 1 : 0;
      const sandbox = this.motion.sandboxCards[index];
      if (sandbox) {
        dealt.push({ kind: 'SANDBOX', card: sandbox, perspective: 'AFF' });
        dealt.push({ kind: 'SANDBOX', card: sandbox, perspective: 'NEG' });
      }
    }
    if (dealt.length === 0) return;
    this.update({
      dealtCards: dealt,
      commitments: {
        ...this.state.commitments,
        [stage.pairId ?? `P${stageIndex}`]: {
          AFF: { hash: `demo-${stageIndex}-aff`, stageIndex, pairId: stage.pairId ?? '', createdAt: Date.now() },
          NEG: { hash: `demo-${stageIndex}-neg`, stageIndex, pairId: stage.pairId ?? '', createdAt: Date.now() },
        },
      },
      eventLog: [...this.state.eventLog, event('CARD_DEALT', stageIndex)],
    });
  }

  private revealPairForStage(stageIndex: number): void {
    const stage = stageOf(stageIndex);
    if (!stage?.pairId) return;
    const pairStages = getPairStages(stage.pairId);
    const reveals: RevealedCard[] = [];
    for (const pairStage of pairStages) {
      const constructive = this.motion.constructiveCards.find((c) => c.stageIndex === pairStage.index);
      const crossEx = this.motion.crossExCards.find((c) => c.stageIndex === pairStage.index);
      if (constructive) {
        const side = pairStage.speakerSide;
        reveals.push({
          pairId: pairStage.pairId ?? '',
          stageIndex: pairStage.index,
          side,
          salt: 'demo-salt',
          card: { kind: 'CONSTRUCTIVE', card: constructive },
          hash: `demo-${pairStage.index}-${side}`,
          revealedAt: Date.now(),
        });
      }
      if (crossEx) {
        const side = pairStage.speakerSide;
        reveals.push({
          pairId: pairStage.pairId ?? '',
          stageIndex: pairStage.index,
          side,
          salt: 'demo-salt',
          card: { kind: 'CROSS_EXAM', card: crossEx },
          hash: `demo-${pairStage.index}-${side}`,
          revealedAt: Date.now(),
        });
      }
    }
    if (reveals.length === 0) return;
    const already = new Set(this.state.reveals.map((r) => `${r.pairId}:${r.stageIndex}:${r.side}`));
    const merged = [...this.state.reveals, ...reveals.filter((r) => !already.has(`${r.pairId}:${r.stageIndex}:${r.side}`))];
    this.update({
      reveals: merged,
      eventLog: [...this.state.eventLog, event('CARDS_REVEALED', stageIndex)],
    });
  }

  private searchDocs(query: string) {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const docs = [
      ...this.motion.evidenceLibrary.map((e) => ({
        id: e.id,
        title: e.shortTitle,
        text: e.exactQuoteOrStats,
        kind: 'EVIDENCE' as const,
        tags: e.tags ?? [],
        side: undefined,
        stageIndex: undefined,
        score: 1,
        matchedTerms: [] as string[],
      })),
      ...this.motion.constructiveCards.map((c) => ({
        id: c.cardId,
        title: c.title,
        text: `${c.coreMission} ${c.modularScriptBlocks.bodyModule}`,
        kind: 'CARD' as const,
        tags: c.pillTags ?? [],
        side: undefined,
        stageIndex: c.stageIndex,
        score: 1,
        matchedTerms: [] as string[],
      })),
    ];
    return docs
      .filter((doc) =>
        `${doc.title} ${doc.text} ${doc.tags.join(' ')} ${doc.id}`.toLowerCase().includes(q),
      )
      .slice(0, 8);
  }
}

function buildMockReport(motion: MotionPackage, state: ArenaUiState): string {
  const started = state.startedAt ?? Date.now();
  const finished = state.finishedAt ?? Date.now();
  const cardList = state.reveals
    .map((reveal) => {
      const title =
        reveal.card.kind === 'CONSTRUCTIVE'
          ? (reveal.card.card as ConstructiveStrategyCard).title
          : reveal.card.kind === 'CROSS_EXAM'
            ? `质询卡 ${reveal.card.card.cardId}（${reveal.card.card.mode === 'BLIND_RECON' ? '全盲侦察' : '定向克制'}）`
            : (reveal.card.card as SandboxCard).title;
      return `- 环节 ${reveal.stageIndex} · ${sideLabel(reveal.side)} · ${title}`;
    })
    .join('\n');
  return `# 纵横杯跟场诊断战报（演示）

**辩题**：${motion.motion}
**房间**：${state.roomId ?? '—'}
**时间**：${new Date(started).toLocaleString('zh-CN')} → ${new Date(finished).toLocaleString('zh-CN')}
**完成环节**：${state.completedStages.join(' / ') || '—'}

## 一、双盲揭示复盘

${cardList || '（尚未揭示任何底牌）'}

## 二、口径告警

${state.alerts.length ? state.alerts.map((a) => `- [环节 ${a.stageIndex}] ${sideLabel(a.side)}：${a.message}`).join('\n') : '未发现口径错误。'}

## 三、教师批注

${state.annotations.length ? state.annotations.map((a) => `- ${a.authorName}：${a.text}`).join('\n') : '暂无批注。'}

## 四、必打论据覆盖

- 正方：${state.manualEvidenceChecks.AFF.join('、') || '未勾选'}
- 反方：${state.manualEvidenceChecks.NEG.join('、') || '未勾选'}

> 本战报由演示后端生成，仅用于验证导出流程。
`;
}

async function copyText(text: string): Promise<void> {
  try {
    await globalThis.navigator?.clipboard?.writeText(text);
  } catch {
    /* 剪贴板不可用时忽略 */
  }
}

/** 创建演示后端（供 App 在 ?demo=1 时使用） */
export function createMockBackend(): MockRoomBackend {
  return new MockRoomBackend();
}

export { CAPABILITY_NOTES, emptyState };
